import React, {useMemo, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
        MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import {AddOutlined, ArrowDownwardOutlined, ArrowUpwardOutlined, DeleteOutlineOutlined,
        WarningAmberOutlined} from "@mui/icons-material";

import {Grey5, Grey6} from "../../theme/styles";

// The approvers of one cell of the matrix -- one (record type x object type) square.
//
// A CELL IS NOT EDITED IN THE GRID. A slot carries a persona, a meaning, a label, an order and
// possibly a quorum, which is more than a cell can hold; and inline editing of something that needs
// approval to take effect invites the wrong mental model.
//
// THE PERSONA PICKER IS A MODE CHOOSER PLUS A LIST, not one control that changes what it queries.
// That is the house pattern -- TermVisibilityTab uses three separate components rather than one
// polymorphic picker -- and it keeps the three vocabularies from bleeding into each other.
//
// Design: cres-t5/docs/approvals/design.md

type Row = Record<string, any>;

export const MEANINGS = ["Reviewed", "Approved"];

// "Authored" WAS OFFERED AND IS NOT ANY MORE. The author of a record is excluded from signing it, so
// the slot was either unsignable or signed by somebody attesting authorship of work they did not
// author. Server: cres_t5/service/approval_matrix.py RETIRED_MEANINGS.
//
// A cell configured before the retirement still holds the word, and a select whose value is not
// among its options renders BLANK -- so the person editing would see an empty Meaning, change
// something unrelated, and save a meaning they never chose. It stays in the list, marked, until
// the save that normalises it.
export function meaningChoices(current?: string) {
    const chosen = current || "Approved";
    return MEANINGS.includes(chosen)
        ? MEANINGS.map(meaning => ({value: meaning, label: meaning}))
        : [...MEANINGS.map(meaning => ({value: meaning, label: meaning})),
           {value: chosen, label: `${chosen} (retired)`}];
}
export const PERSONA_MODES = [
    {key: "Position", label: "Position"},
    {key: "UserGroup", label: "User group"},
    {key: "WorkGroup", label: "Work group"},
];

/** Slots grouped by stage, in reading order, with quorum groups kept adjacent. */
export function byStage(slots: Row[]) {
    const stages = [...new Set(slots.map(slot => slot.Stage ?? 1))].sort((a, b) => a - b);
    return stages.map(stage => ({
        stage,
        slots: slots.filter(slot => (slot.Stage ?? 1) === stage),
    }));
}

/**
 * What is wrong with this cell, in words, or null.
 *
 * REPORTED, NOT REFUSED, except for the quorum arithmetic. A persona with no members is a legitimate
 * thing to configure -- a hire may be imminent -- and a record it governs will WAIT rather than
 * fail. A quorum asking for more signatures than it has slots is different: it can never be
 * satisfied by anybody, so the server refuses it and the dialog should say so before the round trip.
 */
export function cellProblems(slots: Row[], personaMembers: (mode: string, usid: string) => number | null) {
    const problems: string[] = [];

    slots.forEach((slot, index) => {
        if (!slot.PersonaValue) problems.push(`Approver ${index + 1} names no persona.`);
    });

    // Named by position, not by GROUP NAME. The name is an internal handle the editor generates and
    // never shows, so quoting it in a problem would send somebody looking for a "group-2" that
    // appears nowhere on the screen.
    byStage(slots).forEach(({stage}) => {
        groupsInStage(slots, stage).forEach((group, at) => {
            if (group.required > group.members.length) {
                problems.push(`Any-of group ${at + 1} in stage ${stage} asks for ${group.required} of `
                    + `${group.members.length} signatures, which cannot be satisfied.`);
            }
        });
    });

    const empty = slots
        .filter(slot => slot.PersonaValue && personaMembers(slot.PersonaMode, slot.PersonaValue) === 0)
        .map(slot => slot.PersonaName || "that persona");
    if (empty.length) {
        problems.push(`Nobody currently holds ${[...new Set(empty)].join(", ")}. Records will wait `
            + `rather than fail, until somebody is assigned.`);
    }

    return problems.length ? problems : null;
}

/**
 * The options a slot's persona picker offers.
 *
 * KEEPS WHATEVER THE SLOT ALREADY NAMES, even when the server would not offer it today. Positions
 * are filtered to the Data Catalog focus area, and a persona can also be renamed away or fall out
 * of scope -- so a cell configured earlier can name something the picker no longer lists. A Select
 * whose value is absent from its options renders EMPTY, and saving that cell would then quietly
 * drop an approver somebody had deliberately put there.
 *
 * So it is added back, marked, and the person editing gets to decide whether to change it.
 */
export function choicesFor(personas: Row[], slot: Row) {
    const forMode = (personas ?? []).filter((one: Row) => one.Mode === slot.PersonaMode);
    if (!slot.PersonaValue || forMode.some((one: Row) => one.USID === slot.PersonaValue)) {
        return forMode;
    }
    return [...forMode, {
        USID: slot.PersonaValue,
        Name: `${slot.PersonaName || "Not listed"} — no longer offered`,
        Members: null,
    }];
}

/** The any-of groups within one stage, with their members and the count they require. */
export function groupsInStage(slots: Row[], stage: number) {
    const byName = new Map<string, Row[]>();
    slots.filter(slot => (slot.Stage ?? 1) === stage && slot.QuorumGroup)
        .forEach(slot => byName.set(slot.QuorumGroup, [...(byName.get(slot.QuorumGroup) ?? []), slot]));

    return [...byName.entries()].map(([name, members]) => ({
        name,
        members,
        // ONE COUNT PER GROUP, even though the column lives on every row. The server takes the
        // maximum, so rows that disagree are silently ignored; the editor writes the same value to
        // every member and never lets them diverge in the first place.
        required: Math.max(...members.map(one => Number(one.QuorumRequired) || 0)) || 1,
    }));
}

/**
 * The counts made consistent with the groups as they now stand.
 *
 * THE COUNT LIVES ON EVERY ROW BUT ONLY ONE VALUE IS USED -- the server takes the maximum. So a
 * structural change is where the rows can silently drift apart or become unsatisfiable: delete a
 * member of "any 2 of 3" and the remaining rows still ask for 2 of 2, which is no longer a quorum
 * at all; move one out and they can ask for more signatures than there are slots. Every structural
 * edit passes through here so what is stored is what the editor is showing.
 */
export function clampQuorums(slots: Row[]): Row[] {
    const members = new Map<string, Row[]>();
    slots.forEach(slot => {
        if (!slot.QuorumGroup) return;
        const key = `${slot.Stage ?? 1}::${slot.QuorumGroup}`;
        members.set(key, [...(members.get(key) ?? []), slot]);
    });

    const resolved = new Map<string, number>();
    members.forEach((rows, key) => {
        const asked = Math.max(...rows.map(one => Number(one.QuorumRequired) || 0)) || 1;
        resolved.set(key, Math.min(Math.max(asked, 1), rows.length));
    });

    return slots.map(slot => {
        const wanted = slot.QuorumGroup
            ? resolved.get(`${slot.Stage ?? 1}::${slot.QuorumGroup}`)
            : null;
        return wanted === (slot.QuorumRequired ?? null) ? slot : {...slot, QuorumRequired: wanted};
    });
}

/**
 * The counts a group may be set to: 1 up to its size.
 *
 * A CLOSED SET, SO IT IS A SELECT. It was a number field, and the native spinner inside a 70px
 * `size="small"` input is two arrows about six pixels tall that only appear on hover -- reachable
 * with a deliberate aim and not otherwise, which reads as a control that does not respond. The
 * valid values here are 1..size and size is typically two or three, so a list shows all of them,
 * is always hittable, and cannot be typed wrong in the first place.
 *
 * A count above the size can only arrive from a cell stored before the editor clamped, and it stays
 * in the list rather than rendering blank -- the same reason a retired Meaning stays.
 */
export function countChoices(current: number, size: number) {
    const choices = Array.from({length: Math.max(size, 1)}, (_, at) => at + 1);
    return choices.includes(current) || !current ? choices : [...choices, current];
}

/** A group name not already in use. Names are internal -- nothing shows them to a reader. */
export function nextGroupName(slots: Row[]) {
    const used = new Set(slots.map(slot => slot.QuorumGroup).filter(Boolean));
    for (let n = 1; ; n += 1) {
        const name = `group-${n}`;
        if (!used.has(name)) return name;
    }
}

export default function ApprovalCellEditor({open, heading, description, cell, personas, readOnly,
                                           requireSlots = false, saveLabel = "Apply to draft",
                                           selfGoverning = false, banded = false, amountUpTo = null,
                                            onSave, onClose}: any) {
    // APPROVAL BY AMOUNT: a banded cell carries the total it governs up to. Typed here, checked by
    // the server (CC-OOAM-BAND) as well, because a band of nothing governs nothing.
    const [band, setBand] = useState<string>(amountUpTo == null ? "" : String(amountUpTo));
    const bandProblem = banded && !(Number(band) > 0) ? "Give the amount this band is up to." : null;
    // The stored value is NOT clamped on the way in -- a cell that arrives asking for more
    // signatures than it has slots is reported by cellProblems rather than quietly rewritten.
    const [slots, setRawSlots] = useState<Row[]>(() => (cell?.Slots ?? []).map((one: Row) => ({...one})));
    const setSlots = (next: Row[]) => setRawSlots(clampQuorums(next));

    const membersOf = useMemo(() => {
        const index = new Map<string, number>();
        (personas ?? []).forEach((one: Row) => index.set(`${one.Mode}::${one.USID}`, one.Members));
        return (mode: string, usid: string) => {
            const found = index.get(`${mode}::${usid}`);
            return found === undefined ? null : found;
        };
    }, [personas]);

    const othersOf = useMemo(() => {
        const index = new Map<string, number>();
        (personas ?? []).forEach((one: Row) => index.set(`${one.Mode}::${one.USID}`, one.OtherHolders));
        return (mode: string, usid: string) => {
            const found = index.get(`${mode}::${usid}`);
            return found === undefined ? null : found;
        };
    }, [personas]);

    // THE LOCK-OUT, WARNED ABOUT WHILE IT CAN STILL BE AVOIDED. This row decides who approves changes
    // to the matrix, and whoever edits it authors the revision that carries it. An author does not
    // approve their own record -- so naming only personas that nobody else holds produces a matrix
    // whose next revision nobody can sign.
    //
    // A WARNING, NOT A REFUSAL, because it is recoverable: putting one more person in that position
    // clears it, and a realm may be one hire away from meaning to do exactly this. Refusing would
    // also be refusing on a prediction about a membership table that changes without us.
    const named = slots.filter(slot => slot.PersonaValue);
    const lockOutProblem = selfGoverning && named.length
        && named.every(slot => othersOf(slot.PersonaMode, slot.PersonaValue) === 0)
        ? `Only you hold ${[...new Set(named.map(slot => slot.PersonaName || "that persona"))].join(", ")}.`
          + " You author changes to this matrix and an author does not approve their own record, so"
          + " until somebody else holds one of these, no later revision can be approved."
        : null;

    const emptyProblem = requireSlots && slots.length === 0
        ? "Name at least one approver. A matrix that names nobody for itself can never be changed."
        : null;
    const problems = [...(cellProblems(slots, membersOf) ?? []),
                      ...(emptyProblem ? [emptyProblem] : []),
                      ...(bandProblem ? [bandProblem] : []),
                      ...(lockOutProblem ? [lockOutProblem] : [])];
    const blocking = problems.filter(one => one.includes("cannot be satisfied")
                                         || one.includes("names no persona")
                                         || one === emptyProblem
                                         || one === bandProblem);

    const update = (index: number, changes: Row) =>
        setSlots(slots.map((slot, at) => at === index ? {...slot, ...changes} : slot));

    const move = (index: number, by: number) => {
        const next = [...slots];
        const target = index + by;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setSlots(next);
    };

    const add = (stage: number) => setSlots([...slots, {
        Stage: stage, PersonaMode: "Position", PersonaValue: "", Meaning: "Approved",
    }]);

    const grouped = byStage(slots);
    const stages = grouped.map(one => one.stage);
    const multiStage = stages.length > 1;

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
            {/* THE HEADING COMES FROM THE CALLER because only the caller knows what the row is. The
                Catalog's editor built it from targetType and class name, which works there -- the
                target type is 'Rule' or 'Term' and means something to a reader. In Foundation every
                row has the same target type, 'Object', so composing it here produced the heading
                "Object · every object type" over the sentence "Records of this type classified every
                object type need these signatures." Both grammatical, neither English. */}
            <DialogTitle sx={{fontSize: 16}}>{heading}</DialogTitle>

            <DialogContent>
                <Typography style={{fontSize: 13, color: Grey5, marginBottom: 14}}>
                    {description}
                    {" "}Stages run in order; approvers within a stage sign in parallel.
                </Typography>

                {banded ? (
                    <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 16}}>
                        <Typography style={{fontSize: 13}}>Orders with a total up to</Typography>
                        <TextField size="small" type="number" value={band} disabled={readOnly}
                                   slotProps={{htmlInput: {min: 0, step: "any"}}} sx={{width: 160}}
                                   onChange={e => setBand(e.target.value)}/>
                        <Typography style={{fontSize: 13, color: Grey6}}>
                            in the order's own currency
                        </Typography>
                    </div>
                ) : null}

                {slots.length === 0 ? (
                    <Box sx={{border: "1px dashed #D8DDE4", borderRadius: "8px", padding: "24px",
                              textAlign: "center", marginBottom: 2}}>
                        <Typography style={{fontSize: 13, color: "#B54708"}}>
                            No approvers. Records of this type release directly, unwitnessed.
                        </Typography>
                    </Box>
                ) : null}

                {grouped.map(({stage, slots: inStage}) => (
                    <div key={stage} style={{marginBottom: 18}}>
                        {multiStage ? (
                            <Typography style={{fontSize: 12, fontWeight: 600, color: Grey6,
                                                marginBottom: 6}}>
                                Stage {stage}
                                {inStage[0]?.StageLabel ? ` — ${inStage[0].StageLabel}` : ""}
                            </Typography>
                        ) : null}

                        {inStage.map(slot => {
                            const index = slots.indexOf(slot);
                            const members = slot.PersonaValue
                                ? membersOf(slot.PersonaMode, slot.PersonaValue) : null;
                            const choices = choicesFor(personas ?? [], slot);
                            return (
                                <div key={index} style={{display: "flex", gap: 8, alignItems: "center",
                                                         marginBottom: 8}}>
                                    <TextField select size="small" label="Kind" value={slot.PersonaMode}
                                               disabled={readOnly} sx={{width: 130}}
                                               onChange={e => update(index, {PersonaMode: e.target.value,
                                                                             PersonaValue: "",
                                                                             PersonaName: ""})}>
                                        {PERSONA_MODES.map(mode => (
                                            <MenuItem key={mode.key} value={mode.key}>{mode.label}</MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField select size="small" label="Who" value={slot.PersonaValue || ""}
                                               disabled={readOnly} sx={{flex: 1, minWidth: 180}}
                                               onChange={e => {
                                                   const chosen = choices.find((one: Row) => one.USID === e.target.value);
                                                   update(index, {PersonaValue: e.target.value,
                                                                  PersonaName: chosen?.Name});
                                               }}>
                                        {choices.map((one: Row) => (
                                            <MenuItem key={one.USID} value={one.USID}>
                                                {one.Name}{one.Members === 0 ? " — nobody holds this" : ""}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField select size="small" label="Meaning" value={slot.Meaning || "Approved"}
                                               disabled={readOnly} sx={{width: 130}}
                                               onChange={e => update(index, {Meaning: e.target.value})}>
                                        {meaningChoices(slot.Meaning).map(choice => (
                                            <MenuItem key={choice.value} value={choice.value}>
                                                {choice.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {/* A SELECT, NOT FREE TEXT. Typing the group name on each row
                                        meant one typo split a group in two and quietly required
                                        MORE signatures than intended, with nothing to notice it. */}
                                    {/* displayEmpty, because "" IS A CHOICE HERE. A MUI select
                                        renders an empty-string value as an empty box unless told
                                        otherwise -- so "Its own requirement", which is the state
                                        every row starts in, showed as a blank field. Absent is a
                                        fine reading for the persona picker above; it is the wrong
                                        one for a field whose default answer is a real answer. */}
                                    <TextField select size="small" label="Counts as"
                                               value={slot.QuorumGroup || ""} disabled={readOnly}
                                               slotProps={{select: {displayEmpty: true},
                                                           inputLabel: {shrink: true}}}
                                               sx={{width: 210}}
                                               onChange={e => {
                                                   const chosen = e.target.value;
                                                   if (chosen === "__new") {
                                                       update(index, {QuorumGroup: nextGroupName(slots),
                                                                      QuorumRequired: 1});
                                                   } else {
                                                       update(index, {QuorumGroup: chosen || null,
                                                                      QuorumRequired: chosen
                                                                          ? (groupsInStage(slots, stage)
                                                                              .find(g => g.name === chosen)?.required ?? 1)
                                                                          : null});
                                                   }
                                               }}>
                                        <MenuItem value="">Its own requirement</MenuItem>
                                        {groupsInStage(slots, stage)
                                            .filter(group => group.name !== slot.QuorumGroup)
                                            .map((group, at) => (
                                                <MenuItem key={group.name} value={group.name}>
                                                    Part of any-of group {at + 1}
                                                </MenuItem>
                                            ))}
                                        {slot.QuorumGroup ? (
                                            <MenuItem value={slot.QuorumGroup}>
                                                Part of any-of group {groupsInStage(slots, stage)
                                                    .findIndex(g => g.name === slot.QuorumGroup) + 1}
                                            </MenuItem>
                                        ) : null}
                                        <MenuItem value="__new">Part of a new any-of group…</MenuItem>
                                    </TextField>

                                    {members === 0 ? (
                                        <Tooltip title="Nobody holds this persona. Records will wait, not fail.">
                                            <WarningAmberOutlined sx={{fontSize: 18, color: "#B54708"}}/>
                                        </Tooltip>
                                    ) : null}

                                    {!readOnly ? (
                                        <>
                                            <IconButton size="small" onClick={() => move(index, -1)}>
                                                <ArrowUpwardOutlined sx={{fontSize: 16}}/></IconButton>
                                            <IconButton size="small" onClick={() => move(index, 1)}>
                                                <ArrowDownwardOutlined sx={{fontSize: 16}}/></IconButton>
                                            <IconButton size="small"
                                                        onClick={() => setSlots(slots.filter((_, at) => at !== index))}>
                                                <DeleteOutlineOutlined sx={{fontSize: 16}}/></IconButton>
                                        </>
                                    ) : null}
                                </div>
                            );
                        })}

                        {/* ONE LINE PER GROUP, reading as the sentence it means. The count used to
                            sit in a box on every row labelled "of", which read as the group SIZE
                            and was in fact the number required. */}
                        {groupsInStage(slots, stage).map((group, at) => (
                            <div key={group.name}
                                 style={{display: "flex", alignItems: "center", gap: 8, margin: "4px 0 10px 0",
                                         paddingLeft: 10, borderLeft: "3px solid #D8DDE4"}}>
                                <Typography style={{fontSize: 13}}>Any-of group {at + 1}: any</Typography>
                                <TextField select size="small" value={group.required}
                                           disabled={readOnly} sx={{width: 72}}
                                           onChange={e => {
                                               const wanted = Number(e.target.value);
                                               setSlots(slots.map(one => one.QuorumGroup === group.name
                                                   ? {...one, QuorumRequired: wanted} : one));
                                           }}>
                                    {countChoices(group.required, group.members.length).map(choice => (
                                        <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                                    ))}
                                </TextField>
                                <Typography style={{fontSize: 13}}>
                                    of these {group.members.length} must sign
                                    {group.members.length === 1 ? " — a group of one is just a required approver" : ""}
                                </Typography>
                            </div>
                        ))}

                        {!readOnly ? (
                            <Button size="small" startIcon={<AddOutlined sx={{fontSize: 15}}/>}
                                    onClick={() => add(stage)} sx={{textTransform: "none", fontSize: 12}}>
                                Add approver to stage {stage}
                            </Button>
                        ) : null}
                    </div>
                ))}

                {!readOnly ? (
                    <Button size="small" startIcon={<AddOutlined sx={{fontSize: 15}}/>}
                            onClick={() => add(slots.length ? Math.max(...stages) + 1 : 1)}
                            sx={{textTransform: "none", fontSize: 12}}>
                        {slots.length ? "Add a later stage" : "Add an approver"}
                    </Button>
                ) : null}

                {problems.length ? (
                    <Box sx={{marginTop: 2, padding: "10px 12px", borderRadius: "6px",
                              background: "#FFFAEB", border: "1px solid #FEDF89"}}>
                        {problems.map(problem => (
                            <Typography key={problem} style={{fontSize: 12, color: "#B54708"}}>
                                {problem}
                            </Typography>
                        ))}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} sx={{textTransform: "none"}}>
                    {readOnly ? "Close" : "Cancel"}
                </Button>
                {!readOnly ? (
                    <Button variant="contained" disabled={blocking.length > 0}
                            onClick={() => onSave(slots.map((slot, position) => ({...slot, Position: position})),
                                                  banded ? band : null)}
                            sx={{textTransform: "none"}}>
                        {saveLabel}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
